/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect } from 'react';
import Dropdown from 'rc-dropdown';
import Menu, { Item as MenuItem } from 'rc-menu';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import PropTypes from 'prop-types';
import 'rc-dropdown/assets/index.css';
import { useTheme } from 'emotion-theming';

import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { FOCUS_FILL_VAR } from '../../../../shared/theme/focusStyles';
import { claimArrowKeys, hasArrowKeyOwner } from '../../utils/arrowKeyOwnership';

const DropdownIcon = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
`;

const DropdownContainerPrimary = styled('div')`
  display: inline-flex;
  align-items: center;
  ${(props) => props.visible && `
    cursor: pointer;
    padding: 2px 4px;
    margin: -2px -4px;
    border-radius: 4px;
    &:focus { outline: none; }
    &:focus-visible {
      outline: none;
      background: ${FOCUS_FILL_VAR};
      box-shadow: inset 0 0 0 2px #BFDBFE;
    }
  `}
`;

const MenuWrapper = styled('div')`
  display: flex;
  overflow: hidden;
  flex-direction: column;
`;

const CustomDivider = styled.div`
  height: 1px;
  margin: 8px 4px;
  background: ${({ theme }) => theme.colors.borderLight};
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  /** No fill of its own: the MenuItem paints the whole row, so the highlight
   * and hover cover the full option instead of an inner box. */
  border-radius: 4px;
  width: 100%;
  padding: 4px;
`;

const DeleteIconContainer = styled.div`
  display: flex;
  align-items: center;
  border-radius: 4px;
  padding: 4px;
`;

const ArrowDiv = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`;

const MenuText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 12pt;
  /** Light: the current option is the grey row. Dark flips it - the current
   * option is white and the rest are grey. */
  color: ${({ selected, theme }) => {
    if (theme.isDark) return selected ? theme.colors.textStrong : theme.colors.textSecondary;
    return selected ? theme.colors.textSecondary : theme.colors.text;
  }};
  margin-bottom: 0px;
  font-size: 16px;
`;

const SwapPropDropdownCircuit = ({
  size,
  primaryRowSwitch,
  setPrimaryRowSwitch,
  rowName,
  updateColumnName,
  name,
  unit,
  visible,
}) => {
  const { colors, isDark } = useTheme();
  const menuConfig = [
    {
      condition: rowName === 'reps' || rowName === 'sets',
      items: [
        {
          label: 'Reps',
          selected: rowName === 'reps',
          action: () => updateColumnName(rowName, 'reps'),
        },
        {
          label: 'Sets',
          selected: rowName === 'sets',
          action: () => updateColumnName(rowName, 'sets'),
        },
      ],
    },
  ];

  // Get the format items based on the condition
  const formatItems = menuConfig
    .filter((config) => config.condition)
    .flatMap((config) => config.items);

  const isMainMenu = rowName === 'reps' || rowName === 'sets';

  const deleteAction = () => {
    const flippedStatus = !primaryRowSwitch[rowName];
    const newSwitch = { ...primaryRowSwitch };
    newSwitch[rowName] = flippedStatus;
    setPrimaryRowSwitch(newSwitch);
  };

  /** Controlled open + keyboard navigation (arrow/Enter/Escape). */
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  /** Mouse hover, tracked alongside the keyboard cursor so either one fills
   * the whole row. 'delete' marks the Delete row. */
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const navItems = isMainMenu
    ? [...formatItems, { action: deleteAction }]
    : [{ action: deleteAction }];

  /** Open on the current value, so the highlighted row is the checked one. */
  useEffect(() => {
    if (open) {
      const selectedIndex = navItems.findIndex((i) => i.selected);
      setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    } else {
      setHoveredIndex(null);
    }
  }, [open]);

  /** Close when the calendar scrolls, like the calendar's other popups - the
   * menu is portaled, so it would otherwise float away from its row. Capture
   * phase because the calendar list scrolls in its own container. Scrolls
   * inside the menu itself (`.rc-dropdown`, the portaled overlay) don't count:
   * the pace list is long enough to scroll. */
  useEffect(() => {
    if (!open) return undefined;
    const handleScroll = (e) => {
      if (e.target?.closest?.('.rc-dropdown')) return;
      setOpen(false);
    };
    document.addEventListener('scroll', handleScroll, { capture: true, passive: true });
    return () => document.removeEventListener('scroll', handleScroll, { capture: true });
  }, [open]);

  /** Own the arrow keys while open, so useCalendarKeyboardScroll leaves the
   * calendar where it is instead of sliding a row out from under the menu. */
  useEffect(() => {
    if (!open) return undefined;
    return claimArrowKeys();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex((p) => Math.min(p + 1, navItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((p) => Math.max(p - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = navItems[focusedIndex];
        if (item) { item.action(); setOpen(false); }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, focusedIndex, navItems]);

  const menu = (
    <Menu
      style={{
        width: 229,
        overflowY: 'auto',
        marginBottom: '0px',
        border: `1px solid ${colors.borderLight}`,
        background: colors.surfaceElevated,
      }}
    >
      <MenuWrapper style={{ padding: '8px' }}>
        {formatItems.map((item, index) => (
          <MenuItem
            key={`${item.label}-${index}`}
            style={{
              cursor: 'pointer',
              background: (focusedIndex === index || hoveredIndex === index)
                ? colors.surfaceSecondary
                : colors.surfaceElevated,
            }}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            /** Close on selection like the keyboard Enter path — with a
             * controlled `visible` rc-dropdown won't close itself. */
            onClick={() => { item.action(); setOpen(false); }}
          >
            <IconContainer>
              <ArrowDiv>
                <MenuText selected={item.selected}>{item.label}</MenuText>
                {item.selected && (
                  <IcomoonReact iconSet={iconSet} size={12} icon='checkmark' color={isDark ? colors.textStrong : colors.textSecondary} />
                )}
              </ArrowDiv>
            </IconContainer>
          </MenuItem>
        ))}
        {formatItems.length > 0 && <CustomDivider />}
        <MenuItem
          style={{
            padding: 8,
            cursor: 'pointer',
            background: (focusedIndex === navItems.length - 1 || hoveredIndex === 'delete')
              ? colors.surfaceSecondary
              : colors.surfaceElevated,
          }}
          onMouseEnter={() => setHoveredIndex('delete')}
          onMouseLeave={() => setHoveredIndex(null)}
          onClick={() => { deleteAction(); setOpen(false); }}
        >
          <DeleteIconContainer>
            <IcomoonReact
              iconSet={iconSet}
              size={17}
              icon='trashcan'
              color='#FF0505'
              style={{ marginRight: 5 }}
            />
            <ArrowDiv>
              <MenuText>Delete</MenuText>
            </ArrowDiv>
          </DeleteIconContainer>
        </MenuItem>
      </MenuWrapper>
    </Menu>
  );

  const deleteMenu = (
    <Menu
      style={{
        width: 229,
        overflowY: 'auto',
        marginBottom: '0px',
        border: `1px solid ${colors.borderLight}`,
        background: colors.surfaceElevated,
      }}
    >
      <MenuWrapper>
        <MenuItem
          style={{
            padding: 8,
            cursor: 'pointer',
            background: (focusedIndex === 0 || hoveredIndex === 'delete') ? colors.surfaceSecondary : colors.surfaceElevated,
          }}
          onMouseEnter={() => setHoveredIndex('delete')}
          onMouseLeave={() => setHoveredIndex(null)}
          onClick={() => { deleteAction(); setOpen(false); }}
        >
          <DeleteIconContainer>
            <IcomoonReact
              iconSet={iconSet}
              size={17}
              icon='trashcan'
              color='#FF0505'
              style={{ marginRight: 5 }}
            />
            <ArrowDiv>
              <MenuText>Delete</MenuText>
            </ArrowDiv>
          </DeleteIconContainer>
        </MenuItem>
      </MenuWrapper>
    </Menu>
  );

  return (
    <Dropdown
      overlay={isMainMenu ? menu : deleteMenu}
      aria-haspopup='true'
      trigger={visible ? ['click'] : []} /** Disable trigger if not visible */
      visible={open}
      onVisibleChange={setOpen}
    >
      <DropdownContainerPrimary
        onClick={(e) => e.stopPropagation()}
        visible={visible}
        name={name}
        {...(visible ? {
          role: 'button',
          tabIndex: 0,
          onKeyDown: (e) => {
            /** An open menu owns the keyboard. Focus can still sit on this trigger
             * after the coach mouse-opens another row's menu, and without this
             * the same ArrowDown would open this one too, both menus then
             * arrowing in lockstep. */
            if (hasArrowKeyOwner()) return;
            if (!open && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
              e.preventDefault();
              e.stopPropagation();
              setOpen(true);
            }
          },
        } : {})}
      >
        <DropdownIcon padding={size}>
          {name}
          {' '}
          {unit || null}
          {/* do not show the dropdown icon if the dropdown is disabled */}
          {!visible ? null : (
            <IcomoonReact
              iconSet={iconSet}
              size={7}
              icon='down-arrow'
              color={colors.text}
            />
          )}
        </DropdownIcon>
      </DropdownContainerPrimary>
    </Dropdown>
  );
};

SwapPropDropdownCircuit.propTypes = {
  size: PropTypes.number.isRequired,
  primaryRowSwitch: PropTypes.instanceOf(Object).isRequired,
  setPrimaryRowSwitch: PropTypes.func.isRequired,
  rowName: PropTypes.string.isRequired,
  updateColumnName: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  unit: PropTypes.string.isRequired,
  visible: PropTypes.bool,
};

SwapPropDropdownCircuit.defaultProps = {
  visible: true,
};

export default SwapPropDropdownCircuit;
